import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Navigation, Clock, Milestone } from 'lucide-react';

const stopLetter = (index) => String.fromCharCode(65 + (index % 26));

const formatKm = (meters) => `${(meters / 1000).toFixed(1).replace('.', ',')} km`;
const formatMinutes = (seconds) => {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} Min.`;
  return `${Math.floor(minutes / 60)} Std. ${minutes % 60} Min.`;
};

/**
 * Fahrtdetails wie in Google Maps: Gesamtstrecke, je Abschnitt Start → Ziel mit Ankunftszeit
 * und aufklappbarer Turn-by-Turn-Wegbeschreibung (Google liefert instructions als HTML).
 */
const RouteDirectionsPanel = ({ legs, calculatedTimes = {}, tourStartTime }) => {
  const [expanded, setExpanded] = useState({});

  if (!legs || legs.length === 0) {
    return <div className="text-sm text-gray-500 p-4">Noch keine Route berechnet.</div>;
  }

  const totalDistance = legs.reduce((sum, leg) => sum + (leg.distance?.value || 0), 0);
  const totalDriving = legs.reduce((sum, leg) => sum + (leg.duration?.value || 0), 0);
  const lastTime = calculatedTimes[legs.length - 1];

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-lg border border-gray-200 p-3">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Gesamte Fahrt</div>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold text-gray-900">{formatMinutes(totalDriving)}</span>
          <span className="text-sm text-gray-500">({formatKm(totalDistance)})</span>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {legs.length} Abschnitte{tourStartTime ? ` · Start ${tourStartTime}` : ''}{lastTime?.endTime ? ` · Ende ca. ${lastTime.endTime}` : ''}
        </div>
      </div>

      <ol className="space-y-2">
        {legs.map((leg, index) => {
          const isOpen = !!expanded[index];
          const time = calculatedTimes[index];
          const steps = leg.steps || [];
          return (
            <li key={index} className="bg-white rounded-lg border border-gray-200">
              <button
                type="button"
                onClick={() => setExpanded(prev => ({ ...prev, [index]: !prev[index] }))}
                className="w-full text-left p-3 flex gap-3 items-start hover:bg-gray-50 rounded-lg"
                aria-expanded={isOpen}
              >
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                  {stopLetter(index + 1)}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-xs text-gray-500 truncate">von {leg.start_address?.split(',')[0]}</span>
                  <span className="block text-sm font-medium text-gray-900 truncate">{leg.end_address}</span>
                  <span className="flex flex-wrap gap-x-3 text-xs text-gray-600 mt-1">
                    {leg.duration && <span className="inline-flex items-center gap-1"><Navigation size={12} />{leg.duration.text}</span>}
                    {leg.distance && <span className="inline-flex items-center gap-1"><Milestone size={12} />{leg.distance.text}</span>}
                    {time?.startTime && <span className="inline-flex items-center gap-1"><Clock size={12} />Ankunft {time.startTime}</span>}
                  </span>
                </span>
                {steps.length > 0 && (isOpen ? <ChevronDown size={16} className="text-gray-400 mt-1" /> : <ChevronRight size={16} className="text-gray-400 mt-1" />)}
              </button>
              {isOpen && steps.length > 0 && (
                <ol className="border-t border-gray-100 px-3 py-2 space-y-2">
                  {steps.map((step, stepIndex) => (
                    <li key={stepIndex} className="flex gap-2 text-sm">
                      <span className="text-gray-400 tabular-nums w-5 flex-shrink-0 text-right">{stepIndex + 1}.</span>
                      <span className="flex-1">
                        {/* Google Directions liefert die Anweisung als HTML (z. B. <b>Straße</b>) */}
                        <span className="text-gray-800" dangerouslySetInnerHTML={{ __html: step.instructions || step.html_instructions || '' }} />
                        {step.distance?.text && <span className="block text-xs text-gray-500">{step.distance.text}{step.duration?.text ? ` · ${step.duration.text}` : ''}</span>}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default RouteDirectionsPanel;
